import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Target, Clock, Users, CheckCircle2, XCircle, Award, TrendingUp, BarChart3, Download } from 'lucide-react';
import api from '../api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import MultiplayerGamePDFLayout from './MultiplayerGamePDFLayout';
import { motion } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';

// Card component matching GameOptionsPage design
const Card = ({ children, className = '', delay = 0, index = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered 
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
};

// MetricCard component for stats
const MetricCard = ({ title, value, unit = '', icon: Icon, color = 'text-gray-800', children }) => {
  const { isDarkMode } = useContext(ThemeContext);
  const isHexColor = typeof color === 'string' && color.startsWith('#');
  const iconStyle = isHexColor ? { color: color } : {};
  const iconClassName = isHexColor ? '' : color;
  
  return (
    <Card className="p-3 sm:p-4 md:p-6">
      <div className="flex flex-row items-center justify-center h-full w-full gap-2 sm:gap-3 md:gap-4">
        {Icon && <Icon size={24} className="sm:w-8 sm:h-8 md:w-8 md:h-8" style={{ color: '#a6a6a6' }} />}
        <div className="flex flex-col items-start justify-center">
          <h3 className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm font-semibold uppercase mb-1">{title}</h3>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 text-gray-800 dark:text-gray-200" style={isHexColor && title === 'Total Score' ? { color: color } : {}}>{value}{unit}</p>
          {children}
        </div>
      </div>
    </Card>
  );
};

const MultiplayerGameReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [particles, setParticles] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showTrophy, setShowTrophy] = useState(false);
  const [showScoreAnimation, setShowScoreAnimation] = useState(false);
  const [userTotalScore, setUserTotalScore] = useState(null);
  const canvasRef = useRef(null);
  const { isDarkMode } = useContext(ThemeContext);

  // Get user details from localStorage
  const userStr = typeof window !== 'undefined' ? localStorage.getItem('sat_user') : null;
  const user = userStr ? JSON.parse(userStr) : null;

  // Get room ID from URL
  const roomId = useParams().roomId;
  const userId = user?.user_id;
  
  // Check if user came from MultiplayerGameRoom
  const fromMultiplayerGameRoom = location.state?.fromMultiplayerGameRoom || false;

  // Particle system
  useEffect(() => {
    const createParticle = () => ({
      id: Math.random(),
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      size: Math.random() * 4 + 2,
      color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][Math.floor(Math.random() * 6)]
    });

    const animateParticles = () => {
      setParticles(prev => 
        prev.map(particle => ({
          ...particle,
          x: particle.x + particle.vx,
          y: particle.y + particle.vy,
          size: particle.size * 0.99
        })).filter(particle => particle.size > 0.1)
      );
    };

    if (showFireworks) {
      const interval = setInterval(() => {
        setParticles(prev => [...prev, createParticle()]);
        animateParticles();
      }, 100);

      return () => clearInterval(interval);
    }
  }, [showFireworks]);

  useEffect(() => {
    const fetchGameData = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        
        // Fetch game report data
        const reportResponse = await api.get(`/api/compete/multiplayer_game_report/${roomId}/`);
        const reportData = reportResponse.data;
        
        // Fetch user's total score
        const scoreResponse = await api.get(`/api/user/get_total_score/`);
        setUserTotalScore(scoreResponse.data.total_score);
        
        // Transform API data to match our component structure
        console.log('reportData', reportData.user_stats.duration);
        const transformedData = {
          roomId: reportData.room_details.room_id,
          gameInfo: {
            roomName: reportData.room_details.room_name,
            subject: reportData.room_details.subject,
            difficulty: reportData.room_details.difficulty,
            totalQuestions: reportData.scoreboard[0]?.total_questions || 0,
            completedAt: reportData.room_details.ended_at,
            duration: reportData.user_stats.duration || 0
          },
          players: reportData.scoreboard.map((player, index) => ({
            rank: player.rank,
            userId: player.user_id,
            name: player.name,
            totalScore: player.score,
            correctAnswers: player.correct_answers,
            totalAnswers: player.total_questions,
            accuracy: player.accuracy,
            purse: player.score,
            isWinner: player.rank === 1,
            avatar: getAvatarEmoji(index)
          })),
          questions: reportData.answer_history.map((question, index) => ({
            questionId: question.question_id,
            questionText: question.question_text,
            category: question.category,
            difficulty: question.difficulty,
            questionType: question.question_type,
            feedback: question.feedback,
            correctAnswer: question.correct_answer,
            userAnswer: question.user_answer,
            isCorrect: question.is_correct,
            timeSpent: question.time_spent,
            points: question.points_earned,
            status: question.question_status,
            betAmount: question.bet_amount || 0,
            questionNumber: index + 1,
            options: question.options || null
          }))
        };
        
        setGameData(transformedData);
        
        // Set current player to the actual user
        const currentPlayerData = transformedData.players.find(p => p.userId === userId);
        if (!currentPlayerData) {
          console.error('Current player not found in game data');
        }
        setCurrentPlayer(currentPlayerData);
        
        setLoading(false);
        setShowCelebration(true);
        setShowFireworks(true);
        setShowConfetti(true);
        setShowTrophy(true);
        setShowScoreAnimation(true);
        
        // Hide effects after different durations
        setTimeout(() => setShowFireworks(false), 5000);
        setTimeout(() => setShowConfetti(false), 8000);
        setTimeout(() => setShowTrophy(false), 10000);
        
      } catch (error) {
        console.error('Error fetching game data:', error);
        setLoading(false);
      }
    };
    
    fetchGameData();
  }, [userId, roomId]);

  // Generate base64 PDF for emailing (reduced size for email attachment)
  const generateMultiplayerPdfBase64 = async () => {
    const container = document.getElementById('pdf-layout-container');
    if (!container) {
      throw new Error('Report elements not found for PDF generation.');
    }

    // Wait for images to load
    const images = container.getElementsByTagName('img');
    await Promise.all(Array.from(images).map(img => img.complete ? Promise.resolve() : new Promise(res => { img.onload = res; img.onerror = res; })));

    // Hide on-screen buttons to avoid capturing them
    const actionButtons = document.getElementById('action-buttons');
    if (actionButtons) {
      actionButtons.classList.add('hidden');
    }

    // Reduced scale and quality for smaller email attachment size
    const canvas = await html2canvas(container, { 
      scale: 1.2, // Reduced for smaller file size
      useCORS: true,
      logging: false,
    });

    // Show buttons again immediately after capture
    if (actionButtons) {
      actionButtons.classList.remove('hidden');
    }

    // Reduced JPEG quality from 1.0 to 0.75 for smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.75);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const imgWidth = pageWidth;
    const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

    let heightLeft = imgHeight;
    let position = 0;
    
    // First pdf page
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    // Handle multiple pages
    while (heightLeft > 0) {
      // Move the vertical position of the image slice up by one page height
      position = position - pageHeight; 
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const pdfDataUri = pdf.output('datauristring');
    const base64 = pdfDataUri.includes(',') ? pdfDataUri.split(',')[1] : '';
    return base64;
  };

  // Auto-send email with PDF once report is loaded (only when coming from MultiplayerGameRoom)
  const emailSentRef = useRef(false);
  useEffect(() => {
    const sendEmail = async () => {
      try {
        if (!userId || !roomId || !gameData) return;
        const pdfBase64 = await generateMultiplayerPdfBase64();
        await api.post('/api/compete/send_multiplayer_report_email/', {
          room_id: roomId,
          pdf_base64: pdfBase64,
          fromgameroom: true, // Auto-email is only triggered from game room
        });
      } catch (e) {
        console.error('Failed to email multiplayer report', e);
      }
    };
    // Only auto-email if user came from MultiplayerGameRoom (fromMultiplayerGameRoom flag is true)
    if (fromMultiplayerGameRoom && !emailSentRef.current && !loading && gameData) {
      emailSentRef.current = true;
      sendEmail();
    }
  }, [loading, gameData, roomId, userId, fromMultiplayerGameRoom]);

  // Helper function to calculate game duration
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "0:00";
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format seconds to "Xm Ys" format
  const formatSecondsToMinutesSeconds = (totalSeconds) => {
    if (!totalSeconds || totalSeconds === 0) return '0m 00s';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  };

  // Helper function to get avatar emoji
  const getAvatarEmoji = (index) => {
    const avatars = ["🎯", "⚡", "🌪️", "✨", "🚀", "⭐", "💪", "🔥"];
    return avatars[index % avatars.length];
  };

  const getRankEmoji = (rank) => {
    switch(rank) {
      case 1: return "🥇";
      case 2: return "🥈";
      case 3: return "🥉";
      default: return "🏅";
    }
  };

  const getPerformanceMessage = (accuracy) => {
    if (accuracy >= 80) return "INCREDIBLE! You're a math wizard! 🧙‍♂️";
    if (accuracy >= 60) return "GREAT JOB! You're getting stronger! 💪";
    if (accuracy >= 40) return "GOOD EFFORT! Keep practicing! 🎯";
    return "DON'T GIVE UP! Every expert was once a beginner! 🌟";
  };

  const getScoreColor = (score) => {
    if (score > 0) return "text-green-500";
    if (score < 0) return "text-red-500";
    return "text-gray-500";
  };

  const handleDownloadReport = async () => {
    try {
      const container = document.getElementById('pdf-layout-container');
      if (!container) {
        alert("Report elements not found for PDF generation.");
        return;
      }

      // Wait for images to load
      const images = container.getElementsByTagName('img');
      await Promise.all(Array.from(images).map(img => img.complete ? Promise.resolve() : new Promise(res => { img.onload = res; img.onerror = res; })));

      // Hide on-screen buttons to avoid capturing them
      const actionButtons = document.getElementById('action-buttons');
      if (actionButtons) {
        actionButtons.classList.add('hidden');
      }

      // Capture HTML to Canvas 
      const canvas = await html2canvas(container, { 
        scale: 1.2, // Use higher scale for a much clearer quality on the PDF
        useCORS: true 
      });

      // Show buttons again immediately after capture
      if (actionButtons) {
        actionButtons.classList.remove('hidden');
      }

      // Initialize PDF and calculate dimensions
      const imgData = canvas.toDataURL('image/jpeg', 1.0); // Increased quality to 1.0 for better image quality
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pageWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;
      
      // First pdf page
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Handle multiple pages
      while (heightLeft > 0) {
        // Move the vertical position of the image slice up by one page height
        position = position - pageHeight; 
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Get room ID for filename
      const roomIdStr = roomId || gameData?.roomId || 'Multiplayer';
      pdf.save(`Multiplayer_Report_${roomIdStr}.pdf`);
    } catch (e) {
      console.error('Failed to download report', e);
      const msg = e?.response?.data?.error || e?.message || 'Could not download PDF. Please try again.';
      alert(msg);
    }
  };

  const handleEmailReport = async () => {
    try {
      const pdfBase64 = await generateMultiplayerPdfBase64();

      await api.post('/api/compete/send_multiplayer_report_email/', {
        room_id: roomId,
        pdf_base64: pdfBase64,
        fromgameroom: fromMultiplayerGameRoom, // Send boolean indicating if user came from game room
      });
      alert('Email sent! Please check your inbox.');
    } catch (e) {
      console.error('Failed to email report', e);
      const msg = e?.response?.data?.error || e?.message || 'Could not send email. Please try again.';
      alert(msg);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular pl-0 md:pl-24">
        <Card delay={0} index={0} className="text-center p-4 sm:p-6">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-cyan-500 dark:border-orange-500 mx-auto mb-3 sm:mb-4"></div>
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold font-zonaProRegular text-gray-800 dark:text-gray-200 mb-3 sm:mb-4">Loading Your Report...</h2>
          <div className="flex justify-center space-x-2">
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-cyan-500 dark:bg-orange-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-orange-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
          </div>
        </Card>
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular pl-0 md:pl-24">
        <Card delay={0} index={0} className="text-center p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold font-zonaProRegular text-gray-800 dark:text-gray-200 mb-3 sm:mb-4">No Game Data Found</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6 sm:mb-8">Unable to load the game report.</p>
          <motion.button
            onClick={() => navigate('/lobby-multiplayer')}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-cyan-500 to-blue-500 dark:from-orange-500 dark:to-orange-600 text-white text-sm sm:text-base font-semibold rounded-xl hover:shadow-lg transition-all"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Back to Lobby
          </motion.button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 w-full max-w-7xl mx-auto">

        {/* Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8 relative"
        >
          <h1
            key={`title-${isDarkMode}`}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Multiplayer Game Report
          </h1>
        </motion.div>
        {/* Room Info Header */}
        <Card delay={0.1} index={0} className="mb-8 p-4 sm:p-6">
          <div className="flex flex-col gap-4 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 md:space-x-6 space-y-2 sm:space-y-0">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <span className="text-sm sm:text-base md:text-lg font-semibold text-gray-600 dark:text-gray-400">Room Code:</span>
                <span className="bg-gray-100 dark:bg-gray-700 px-2 sm:px-3 py-1 rounded-lg font-mono text-sm sm:text-base md:text-lg font-bold font-zonaProRegular text-gray-800 dark:text-gray-200">
                  {gameData?.roomId || roomId}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <span className="text-sm sm:text-base md:text-lg font-semibold text-gray-600 dark:text-gray-400">Room Name:</span>
                <span className="text-sm sm:text-base md:text-lg font-bold font-zonaProRegular text-gray-800 dark:text-gray-200">
                  {gameData?.gameInfo?.roomName || 'Challenge'}
                </span>
              </div>
            </div>

            <div className="text-center sm:text-left">
              <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold font-zonaProRegular text-gray-800 dark:text-gray-200 break-words">
                {gameData?.gameInfo?.subject || 'Game Report'} — {gameData?.gameInfo?.difficulty || 'Easy'}
              </h2>
            </div>
          </div>
        </Card>

        {/* Game Info Section */}
        <section className="mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Right Side: Scoreboard Podium */}
            <Card delay={0.2} index={1} className="p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-center text-gray-700 dark:text-gray-200 mb-4 sm:mb-6 font-zonaProUltra" style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f' }}>Leaderboard</h3>
              
              {/* Dynamic layout based on number of players */}
              {gameData.players.length <= 3 ? (
                /* Show only podium for 3 or fewer players */
                <div className="flex items-center justify-center min-h-[250px] sm:min-h-[300px] md:min-h-[350px] lg:min-h-[400px]">
                  <div className="flex items-end justify-center space-x-1 sm:space-x-2 md:space-x-4 lg:space-x-6 xl:space-x-8">
                    {/* 2nd Place */}
                    {gameData.players.find(p => p.rank === 2) && (
                      <div className="flex flex-col items-center">
                        <div className="bg-gray-200 rounded-t-3xl p-2 sm:p-3 md:p-6 w-16 h-14 sm:w-20 sm:h-16 md:w-28 md:h-24 flex flex-col items-center justify-center shadow-lg font-zonaProRegular">
                          <div className="text-2xl sm:text-3xl md:text-4xl">🥈</div>
                          <div className="text-xs sm:text-sm font-bold text-gray-600">2nd</div>
                        </div>
                        <div className="text-center mt-2 sm:mt-3 md:mt-4">
                          <div className="font-bold text-xs sm:text-sm md:text-base font-zonaProRegular text-gray-800 dark:text-gray-200 truncate max-w-[64px] sm:max-w-[80px] md:max-w-none">{gameData.players.find(p => p.rank === 2)?.name}</div>
                          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{gameData.players.find(p => p.rank === 2)?.totalScore} pts</div>
                        </div>
                      </div>
                    )}
                    
                    {/* 1st Place */}
                    {gameData.players.find(p => p.rank === 1) && (
                      <div className="flex flex-col items-center">
                        <div className="bg-yellow-200 rounded-t-3xl p-2 sm:p-4 md:p-6 w-20 h-18 sm:w-24 sm:h-20 md:w-32 md:h-32 flex flex-col items-center justify-center shadow-lg">
                          <div className="text-3xl sm:text-4xl md:text-5xl">🥇</div>
                          <div className="text-xs sm:text-sm md:text-base font-bold text-yellow-700">1st</div>
                        </div>
                        <div className="text-center mt-2 sm:mt-3 md:mt-4">
                          <div className="font-bold text-xs sm:text-sm md:text-base font-zonaProRegular text-gray-800 dark:text-gray-200 truncate max-w-[80px] sm:max-w-[96px] md:max-w-none">{gameData.players.find(p => p.rank === 1)?.name}</div>
                          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{gameData.players.find(p => p.rank === 1)?.totalScore} pts</div>
                        </div>
                      </div>
                    )}
                    
                    {/* 3rd Place */}
                    {gameData.players.find(p => p.rank === 3) && (
                      <div className="flex flex-col items-center">
                        <div className="bg-orange-200 rounded-t-3xl p-2 sm:p-3 md:p-6 w-16 h-12 sm:w-20 sm:h-14 md:w-28 md:h-20 flex flex-col items-center justify-center shadow-lg">
                          <div className="text-xl sm:text-2xl md:text-3xl">🥉</div>
                          <div className="text-xs sm:text-sm font-bold text-orange-700">3rd</div>
                        </div>
                        <div className="text-center mt-2 sm:mt-3 md:mt-4">
                          <div className="font-bold text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200 truncate max-w-[64px] sm:max-w-[80px] md:max-w-none">{gameData.players.find(p => p.rank === 3)?.name}</div>
                          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{gameData.players.find(p => p.rank === 3)?.totalScore} pts</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Show podium + remaining players for more than 3 players */
                <div className="flex flex-col lg:flex-row gap-8 items-start mt-4">
                  {/* Podium for Top 3 */}
                  <div className="w-full lg:w-2/3 flex items-center justify-center min-h-[250px] sm:min-h-[300px] md:min-h-[350px] lg:min-h-[400px]">
                    <div className="flex items-end justify-center space-x-1 sm:space-x-2 md:space-x-4 lg:space-x-6 xl:space-x-8">
                      {/* 2nd Place */}
                      {gameData.players.find(p => p.rank === 2) && (
                        <div className="flex flex-col items-center">
                          <div className="bg-gray-200 rounded-t-3xl p-2 sm:p-3 md:p-6 w-16 h-14 sm:w-20 sm:h-16 md:w-28 md:h-24 flex flex-col items-center justify-center shadow-lg font-zonaProRegular">
                            <div className="text-2xl sm:text-3xl md:text-4xl">🥈</div>
                            <div className="text-xs sm:text-sm font-bold text-gray-600">2nd</div>
                          </div>
                          <div className="text-center mt-2 sm:mt-3 md:mt-4">
                            <div className="font-bold text-xs sm:text-sm md:text-base font-zonaProRegular text-gray-800 truncate max-w-[64px] sm:max-w-[80px] md:max-w-none">{gameData.players.find(p => p.rank === 2)?.name}</div>
                            <div className="text-xs sm:text-sm text-gray-600">{gameData.players.find(p => p.rank === 2)?.totalScore} pts</div>
                          </div>
                        </div>
                      )}
                      
                      {/* 1st Place */}
                      {gameData.players.find(p => p.rank === 1) && (
                        <div className="flex flex-col items-center">
                          <div className="bg-yellow-200 rounded-t-3xl p-2 sm:p-4 md:p-6 w-20 h-18 sm:w-24 sm:h-20 md:w-32 md:h-32 flex flex-col items-center justify-center shadow-lg">
                            <div className="text-3xl sm:text-4xl md:text-5xl">🥇</div>
                            <div className="text-xs sm:text-sm md:text-base font-bold text-yellow-700">1st</div>
                          </div>
                          <div className="text-center mt-2 sm:mt-3 md:mt-4">
                            <div className="font-bold text-xs sm:text-sm md:text-base font-zonaProRegular text-gray-800 truncate max-w-[80px] sm:max-w-[96px] md:max-w-none">{gameData.players.find(p => p.rank === 1)?.name}</div>
                            <div className="text-xs sm:text-sm text-gray-600">{gameData.players.find(p => p.rank === 1)?.totalScore} pts</div>
                          </div>
                        </div>
                      )}
                      
                      {/* 3rd Place */}
                      {gameData.players.find(p => p.rank === 3) && (
                        <div className="flex flex-col items-center">
                          <div className="bg-orange-200 rounded-t-3xl p-2 sm:p-3 md:p-6 w-16 h-12 sm:w-20 sm:h-14 md:w-28 md:h-20 flex flex-col items-center justify-center shadow-lg">
                            <div className="text-xl sm:text-2xl md:text-3xl">🥉</div>
                            <div className="text-xs sm:text-sm font-bold text-orange-700">3rd</div>
                          </div>
                          <div className="text-center mt-2 sm:mt-3 md:mt-4">
                            <div className="font-bold text-xs sm:text-sm md:text-base text-gray-800 truncate max-w-[64px] sm:max-w-[80px] md:max-w-none">{gameData.players.find(p => p.rank === 3)?.name}</div>
                            <div className="text-xs sm:text-sm text-gray-600">{gameData.players.find(p => p.rank === 3)?.totalScore} pts</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rest of the players */}
                  <div className="w-full lg:w-1/3 mt-6 lg:mt-0">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3 sm:mb-4">Other Players</h4>
                    <div className="space-y-2">
                      {gameData.players
                        .filter(p => p.rank > 3)
                        .sort((a, b) => a.rank - b.rank)
                        .map((player) => (
                          <motion.div
                            key={player.userId}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 }}
                            className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 sm:p-3"
                          >
                            <div className="flex items-center space-x-2 sm:space-x-3">
                              <div className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm">
                                {player.rank}
                              </div>
                              <div>
                                <div className="font-semibold text-xs sm:text-sm text-gray-800 dark:text-gray-200 truncate max-w-[120px] sm:max-w-none">{player.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Rank #{player.rank}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-xs sm:text-sm text-gray-800 dark:text-gray-200">{player.totalScore}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">points</div>
                            </div>
                          </motion.div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </Card>
            
            
            {/* Player Stats Card */}
            {currentPlayer ? (
              <Card delay={0.3} index={2} className="p-4 sm:p-6">
                <div className="text-center mb-4 sm:mb-6">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500 dark:text-yellow-400 mx-auto mb-3 sm:mb-4" />
                  </motion.div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 dark:text-gray-200 mb-2 font-zonaProUltra">
                    {getRankEmoji(currentPlayer.rank)} Rank #{currentPlayer.rank}
                  </h3>
                  <div className="text-lg sm:text-xl md:text-2xl font-bold text-cyan-500 dark:text-orange-500 mb-2 font-zonaProUltra">
                    {currentPlayer.name}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    {currentPlayer.isWinner ? "🏆 CHAMPION! 🏆" : `Final Score: ${currentPlayer.totalScore} points`}
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <MetricCard
                    title="Score"
                    value={currentPlayer.totalScore}
                    icon={BarChart3}
                    color="#ff7a00"
                  />
                  <MetricCard
                    title="Accuracy"
                    value={currentPlayer.accuracy}
                    unit="%"
                    icon={Target}
                    color="text-blue-500"
                  />
                  <MetricCard
                    title="Correct Answers"
                    value={`${currentPlayer.correctAnswers}/${currentPlayer.totalAnswers}`}
                    icon={CheckCircle2}
                    color="text-purple-500"
                  />
                  <MetricCard
                    title="Time Spent"
                    value={formatSecondsToMinutesSeconds(gameData.gameInfo.duration)}
                    icon={Clock}
                    color="text-orange-500"
                  />
                </div>
              </Card>
            ) : (
              <Card delay={0.3} index={2}>
                <div className="text-center py-8">
                  <div className="text-gray-500 dark:text-gray-400 text-lg">
                    Player data not available
                  </div>
                </div>
              </Card>
            )}
          </div>
        </section>
        {/* Question Details Section */}
        <section className="mb-10">
          <Card delay={0.4} index={3} className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4 sm:mb-6 text-left font-zonaProUltra" style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f' }}>Question-by-Question Breakdown</h3>
            
            <div className="space-y-4 sm:space-y-6">
              {gameData.questions.map((question, index) => (
                <motion.div
                  key={question.questionId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 border-2 border-gray-100 dark:border-gray-600 hover:border-cyan-200 dark:hover:border-orange-400 transition-all duration-300"
                >
                  {/* Question Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-3 sm:mb-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="bg-cyan-500 dark:bg-orange-500 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm">
                        {question.questionNumber}
                      </div>
                      <div>
                        <div className="font-semibold text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{question.category} • {question.difficulty}</div>
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Question #{question.questionNumber}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {question.status === 'correct' ? (
                        <CheckCircle2 size={20} className="sm:w-6 sm:h-6 text-green-500" />
                      ) : (
                        question.status === 'incorrect' ? (
                        <XCircle size={20} className="sm:w-6 sm:h-6 text-red-500" />
                      ) : (
                        <XCircle size={20} className="sm:w-6 sm:h-6 text-yellow-500" />
                      )
                      )}
                      <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold ${getScoreColor(question.points)}`}>
                        {question.points > 0 ? '+' : ''}{question.points} pts
                      </div>
                    </div>
                  </div>

                  <div className="md:flex md:gap-8">
                    {/* Left Side: Question & Feedback */}
                    <div className="md:w-3/5 lg:w-1/2 space-y-3 sm:space-y-4 md:space-y-5">
                      <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-gray-200 font-zonaProUltra mb-1">Question:</h1>
                      <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-700 dark:text-gray-300 text-left leading-relaxed whitespace-pre-line">
                        {question.questionText}
                      </p>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mt-4 sm:mt-6 p-3 sm:p-4 md:p-5 rounded-lg border-l-4 ${question.status === 'correct' ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20' : question.status === 'incorrect' ? 'border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-yellow-500 dark:border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'}`}
                      >
                        <h3 className={`text-base sm:text-lg md:text-xl font-semibold mb-2 font-zonaProUltra ${question.status === 'correct' ? 'text-green-700 dark:text-green-300' : question.status === 'incorrect' ? 'text-red-700 dark:text-red-300' : 'text-yellow-700 dark:text-yellow-300'}`}>
                          {question.status === 'correct' ? 'CORRECT!' : question.status === 'incorrect' ? 'INCORRECT!' : 'UNANSWERED!'}
                        </h3>
                        <p className={`mb-2 text-sm sm:text-base ${question.status === 'correct' ? 'text-green-700 dark:text-green-300' : question.status === 'incorrect' ? 'text-red-700 dark:text-red-300' : 'text-yellow-700 dark:text-yellow-300'}`}>
                          {question.feedback ? question.feedback : 'Great job on this question!'}
                        </p>
                        <div className={`text-sm sm:text-base md:text-lg font-semibold ${question.status === 'correct' ? 'text-green-700 dark:text-green-300' : question.status === 'incorrect' ? 'text-red-700 dark:text-red-300' : 'text-yellow-700 dark:text-yellow-300'}`}>
                          Points: <span className="font-bold">{question.points > 0 ? `+${question.points}` : question.points}</span>
                        </div>
                      </motion.div>
                    </div>

                    {/* Right Side: Answer Options */}
                    <div className="md:w-2/5 lg:w-1/2 md:pl-4 lg:pl-6 mt-6 sm:mt-8 md:mt-0">
                      <h2 className="text-base sm:text-lg md:text-xl font-zonaProUltra text-gray-700 dark:text-gray-300 mb-3 sm:mb-4">
                        Results:
                      </h2>
                      {question.questionType === 'Grid-in' ? (
                        <div className="space-y-2 sm:space-y-3">
                          {question.isCorrect && (
                            <div className="w-full text-left p-3 sm:p-4 rounded-lg border-2 text-sm sm:text-base font-medium bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300">
                              <span className="font-bold mr-2">Your Answer:</span>{question.userAnswer}
                            </div>
                          )}

                          {!question.isCorrect && question.userAnswer && (
                            <>
                              <div className="w-full text-left p-3 sm:p-4 rounded-lg border-2 text-sm sm:text-base font-medium bg-red-50 dark:bg-red-900/30 border-red-500 dark:border-red-600 text-red-700 dark:text-red-300">
                                <span className="font-bold mr-2">Your Answer:</span>{question.userAnswer}
                              </div>
                              <div className="w-full text-left p-3 sm:p-4 rounded-lg border-2 text-sm sm:text-base font-medium bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300">
                                <span className="font-bold mr-2">Correct Answer:</span>{question.correctAnswer}
                              </div>
                            </>
                          )}

                          {!question.userAnswer && (
                            <div className="w-full text-left p-3 sm:p-4 rounded-lg border-2 text-sm sm:text-base font-medium bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300">
                              <span className="font-bold mr-2">Correct Answer:</span>{question.correctAnswer}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2 sm:space-y-3">
                          {question.options && Object.entries(question.options).map(([key, value]) => {
                            const isUserAnswer = question.userAnswer === value;
                            const isCorrectAnswer = question.correctAnswer === value;

                            let classes = "w-full text-left p-3 sm:p-4 rounded-lg border-2 text-sm sm:text-base font-medium";

                            if (isUserAnswer && !isCorrectAnswer) {
                              classes += ' bg-red-50 dark:bg-red-900/30 border-red-400 dark:border-red-600 text-red-700 dark:text-red-300'; // User's incorrect choice
                            } else if (isCorrectAnswer) {
                              classes += ' bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300'; // The correct answer
                            } else {
                              classes += ' bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 opacity-75'; // Other options
                            }

                            return (
                              <div key={key} className={classes}>
                                <span className="font-bold mr-2">{key})</span>{value}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="flex flex-wrap items-center justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-2 sm:p-3 mt-4 sm:mt-6">
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="flex items-center space-x-1">
                        <Clock size={14} className="sm:w-4 sm:h-4" />
                        <span>{question.timeSpent}s</span>
                      </div>
                    </div>
                    <div className={`font-bold text-sm sm:text-base md:text-lg ${getScoreColor(question.points)}`}>
                      {question.points > 0 ? '+' : ''}{question.points} points
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
        </section>

        {/* Action Buttons Section */}
        <section id="action-buttons" className="mb-10">
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <motion.button
              onClick={() => {
                setShowFireworks(true);
                setTimeout(() => navigate('/lobby-multiplayer'), 1000);
              }}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-cyan-500 to-blue-500 dark:from-orange-500 dark:to-orange-600 text-white text-sm sm:text-base md:text-lg font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Users size={18} className="sm:w-5 sm:h-5" />
              Back to Lobby
            </motion.button>
            <motion.button
              onClick={handleEmailReport}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 text-white text-sm sm:text-base md:text-lg font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <BarChart3 size={18} className="sm:w-5 sm:h-5" />
              Email me this report (PDF)
            </motion.button>
            <motion.button
              onClick={handleDownloadReport}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 text-white text-sm sm:text-base md:text-lg font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}>
              <Download size={18} className="sm:w-5 sm:h-5" />
              Download Report (PDF)
            </motion.button>
            {/* <button
              onClick={() => {
                setShowConfetti(true);
                window.print();
              }}
              className="px-6 py-3 bg-gray-500 text-white font-semibold rounded-xl hover:bg-gray-600 transition-colors duration-200 flex items-center gap-2"
            >
              <BarChart3 size={20} />
              Print Report
            </button>
            <button
              onClick={() => {
                setShowTrophy(true);
                setShowFireworks(true);
                setTimeout(() => navigate('/multiplayer'), 1500);
              }}
              className="px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors duration-200 flex items-center gap-2"
            >
              <Trophy size={20} />
              Play Again
            </button> */}
          </div>
        </section>
      </div>

      {/* --- PDF Report Layout (Hidden from Screen) --- */}
      {/* Calls MultiplayerGamePDFLayout */}
      <div 
        id="pdf-layout-container" 
        style={{ 
          position: 'absolute', 
          top: '0', 
          // Move report far left off-screen to hide it
          left: '-9999px', 
          width: '100%', 
          // Ensure no fixed height prevents content from expanding
          zIndex: '-1000' 
        }}
      >
        <MultiplayerGamePDFLayout 
          gameData={gameData} 
          currentPlayer={currentPlayer}
          getRankEmoji={getRankEmoji}
          userTotalScore={userTotalScore}
        />
      </div>
    </div>
  );
};

export default MultiplayerGameReport;
